import { isExactCodeMatch } from '../utils/exactCodeMatch';
import ApiWithLogging from '../utils/apiWithLogging';

// การตั้งค่าค่ามัดจำของบริษัท
export interface CompanyDepositSettings {
  deposit_amount: number; // ยอดค่ามัดจำเก็บสูงสุดที่กำหนด (บาท)
}

// ข้อมูลค่ามัดจำรวมของบริษัท
export interface CompanyTotalDepositInfo {
  company_id: number;
  company_name: string;
  total_deposit: number;
  deposit_limit: number;
  remaining_deposit: number;
  usage_percentage: number;
  active_rental_count: number;
}

// ข้อมูลผู้ใช้แบบย่อ
export interface UserInfo {
  id: number;
  username: string;
  full_name?: string;
}

// ประเภทข้อมูลบริษัท
export interface Company {
  id: number;
  code?: string; // Added missing code property
  name: string;
  taxId: string;
  address: string;
  phone: string;
  email: string;
  contactPerson: string;
  depositSettings?: CompanyDepositSettings; // การตั้งค่าค่ามัดจำ (สำหรับ frontend form)
  deposit_amount?: number; // ยอดค่ามัดจำที่มาจาก backend
  default_wht_rate?: number; // อัตราหัก ณ ที่จ่าย default (0, 1, 2, 3, 5%)
  defaultWhtRate?: number; // อัตราหัก ณ ที่จ่าย default (จาก backend camelCase)
  // Billing settings
  billing_day?: number; // วันออกบิล (1-28)
  billingDay?: number; // วันออกบิล (จาก backend camelCase)
  payment_terms?: number; // ระยะเวลาชำระ (วัน)
  paymentTerms?: number; // ระยะเวลาชำระ (จาก backend camelCase)
  due_day_of_next_month?: number; // วันครบกำหนดของเดือนถัดไป (1-28) เช่น 5 = วันที่ 5
  dueDayOfNextMonth?: number; // วันครบกำหนดของเดือนถัดไป (จาก backend camelCase)
  flowaccountData?: any; // ข้อมูลทั้งหมดที่ได้จาก Flow Account API
  flowaccountSyncDate?: string; // วันที่ซิงค์ข้อมูลล่าสุดกับ Flow Account
  syncedFromFlow?: boolean; // ซิงค์จาก Flow Account หรือไม่
  customer_type?: string; // ประเภทลูกค้า (snake_case จาก form)
  customerType?: string;  // ประเภทลูกค้า (camelCase จาก backend)
  // New fields
  sales_person_id?: number; // พนักงานขายที่ดูแล (ใช้สำหรับ form)
  salesPersonId?: number; // พนักงานขายที่ดูแล (จาก backend)
  sales_person?: UserInfo; // ข้อมูลพนักงานขาย
  isActive?: boolean; // สถานะ (จาก backend)
  is_active?: boolean; // สถานะ (ใช้งาน / ไม่ใช้งาน) (ใช้สำหรับ form)
  min_required_signers?: number; // จำนวนผู้ลงนามขั้นต่ำ (snake_case จาก backend)
  minRequiredSigners?: number; // จำนวนผู้ลงนามขั้นต่ำ (camelCase จาก backend alias)
  created_by_user_id?: number; // ผู้สร้าง
  createdByUserId?: number; // ผู้สร้าง (จาก backend)
  created_by?: UserInfo; // ข้อมูลผู้สร้าง
  updated_by_user_id?: number; // ผู้อัปเดตล่าสุด
  updatedByUserId?: number; // ผู้อัปเดตล่าสุด (จาก backend)
  updated_by?: UserInfo; // ข้อมูลผู้อัปเดต
  createdAt: string;
  updatedAt?: string; // อาจเป็น null ถ้าไม่เคยแก้ไข
}

// ข้อมูลสำหรับการสร้างบริษัทใหม่หรือแก้ไขบริษัท
export interface CompanyForm {
  code: string;       // เพิ่มรหัสบริษัทตามที่ backend ต้องการ
  name: string;
  taxId: string;      // ใน backend จะเป็น tax_id
  address: string;
  phone: string;      // ข้อมูลเพิ่มเติมที่อาจไม่ได้ใช้ใน backend
  email: string;      // ข้อมูลเพิ่มเติมที่อาจไม่ได้ใช้ใน backend
  contactPerson: string; // ข้อมูลเพิ่มเติมที่อาจไม่ได้ใช้ใน backend
  depositSettings?: CompanyDepositSettings; // การตั้งค่าค่ามัดจำ
  default_wht_rate?: number; // อัตราหัก ณ ที่จ่าย default (0, 1, 2, 3, 5%)
  // Billing settings
  billing_day?: number; // วันออกบิล (1-28)
  payment_terms?: number; // ระยะเวลาชำระ (วัน)
  due_day_of_next_month?: number; // วันครบกำหนดของเดือนถัดไป (1-28)
  sales_person_id?: number; // พนักงานขายที่ดูแล
  is_active?: boolean; // สถานะ (default: true)
  customer_type?: string; // ประเภทลูกค้า
}

// พารามิเตอร์สำหรับการดึงข้อมูลบริษัท
export interface GetCompaniesParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  // Advanced search parameters
  name?: string;
  code?: string;
  tax_id?: string;
  address?: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  synced_from_flow?: boolean;
  created_from?: string;
  created_to?: string;
  is_active?: boolean; // กรองตามสถานะ
  sales_person_id?: number; // กรองตามพนักงานขาย
  _t?: number; // timestamp สำหรับ cache busting
}

// การตอบกลับจากการดึงข้อมูลบริษัท
export interface GetCompaniesResponse {
  items: Company[];
  total: number;
  page: number;
  limit: number;
  // Aggregate stats จาก backend — นับจากข้อมูลทุกหน้าตาม filter ปัจจุบัน
  stats?: {
    active: number;
    inactive: number;
    synced: number;
  };
}

// บริการจัดการข้อมูลบริษัท
const companyService = {
  // ดึงรายการบริษัททั้งหมด
  async getCompanies(params: GetCompaniesParams = {}): Promise<GetCompaniesResponse> {
    // สร้าง query parameters โดยกรอง undefined values ออก
    const queryParams: any = {};
    
    // Basic parameters
    if (params.page !== undefined) queryParams.page = params.page;
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.sort !== undefined) queryParams.sort = params.sort;
    if (params.order !== undefined) queryParams.order = params.order;
    if (params.search !== undefined) queryParams.search = params.search;
    
    // Advanced search parameters
    if (params.name !== undefined) queryParams.name = params.name;
    if (params.code !== undefined) queryParams.code = params.code;
    if (params.tax_id !== undefined) queryParams.tax_id = params.tax_id;
    if (params.address !== undefined) queryParams.address = params.address;
    if (params.contact_person !== undefined) queryParams.contact_person = params.contact_person;
    if (params.phone !== undefined) queryParams.phone = params.phone;
    if (params.email !== undefined) queryParams.email = params.email;
    if (params.synced_from_flow !== undefined) queryParams.synced_from_flow = params.synced_from_flow;
    if (params.created_from !== undefined) queryParams.created_from = params.created_from;
    if (params.created_to !== undefined) queryParams.created_to = params.created_to;
    if (params.is_active !== undefined) queryParams.is_active = params.is_active;
    if (params.sales_person_id !== undefined) queryParams.sales_person_id = params.sales_person_id;
    if (params._t !== undefined) queryParams._t = params._t;
    
    // ตั้งค่า default ถ้าไม่มี
    if (!queryParams.page) queryParams.page = 1;
    if (!queryParams.limit) queryParams.limit = 10;
    
    const response = await ApiWithLogging.get<GetCompaniesResponse>('/companies/', 
      { 
        params: queryParams
      },
      {
        service: 'companyService',
        action: 'view_companies_list',
        entityType: 'company'
      }
    );
    
    return response;
  },

  // ดึงข้อมูลบริษัทตาม id
  async getCompany(id: number): Promise<Company> {
    const response = await ApiWithLogging.get<Company>(`/companies/${id}`, {},
      {
        service: 'companyService',
        action: 'view_company_detail',
        entityType: 'company',
        entityId: id
      }
    );
    return response;
  },

  // สร้างบริษัทใหม่
  async createCompany(data: CompanyForm): Promise<Company> {
    // แปลงข้อมูลจาก frontend format เป็น backend format
    const companyData: any = {
      code: data.code,
      name: data.name,
      tax_id: data.taxId,  // เปลี่ยนจาก taxId เป็น tax_id ตามที่ backend ต้องการ
      address: data.address
    };

    // เพิ่มฟิลด์ optional เฉพาะเมื่อมีข้อมูล
    if (data.contactPerson && data.contactPerson.trim()) {
      companyData.contact_person = data.contactPerson;
    }
    if (data.phone && data.phone.trim()) {
      companyData.phone = data.phone;
    }
    if (data.email && data.email.trim()) {
      companyData.email = data.email;
    }
    // เพิ่มการส่ง deposit amount (รองรับค่า 0)
    if (data.depositSettings && typeof data.depositSettings.deposit_amount === 'number') {
      companyData.deposit_amount = data.depositSettings.deposit_amount;
    }
    // เพิ่ม field ใหม่
    if (data.sales_person_id !== undefined) {
      companyData.sales_person_id = data.sales_person_id;
    }
    if (data.is_active !== undefined) {
      companyData.is_active = data.is_active;
    }
    // เพิ่ม default WHT rate
    if (data.default_wht_rate !== undefined) {
      companyData.default_wht_rate = data.default_wht_rate;
    }
    // เพิ่ม billing fields
    if (data.billing_day !== undefined) {
      companyData.billing_day = data.billing_day;
    }
    if (data.payment_terms !== undefined) {
      companyData.payment_terms = data.payment_terms;
    }
    // KD3 S2496 (FLOW-MD-014): ฟอร์มมีช่อง "วันครบกำหนดชำระ (ของเดือนถัดไป)" และ "ประเภทลูกค้า" แต่ payload ไม่เคยส่ง
    // → กดบันทึกขึ้น "สำเร็จ" แต่ค่าที่เลือกหายเงียบ (DB คงค่าเดิม/NULL) · ส่งเมื่อ key มีมา (null/'' = ตั้งใจล้าง)
    if (data.due_day_of_next_month !== undefined) {
      companyData.due_day_of_next_month = data.due_day_of_next_month ?? null;
    }
    if (data.customer_type !== undefined) {
      companyData.customer_type = data.customer_type || null;
    }

    console.log('CREATE - depositSettings:', data.depositSettings);
    console.log('CREATE - deposit_amount value:', data.depositSettings?.deposit_amount);
    console.log('CREATE - typeof deposit_amount:', typeof data.depositSettings?.deposit_amount);
    console.log('CREATE - Final companyData:', companyData);
    
    const response = await ApiWithLogging.post<Company>('/companies/', companyData, {},
      {
        service: 'companyService',
        action: 'create_company',
        entityType: 'company',
        details: {
          code: data.code,
          name: data.name,
          taxId: data.taxId
        }
      }
    );
    return response;
  },

  // แก้ไขข้อมูลบริษัท
  async updateCompany(id: number, data: CompanyForm, ifMatch?: string): Promise<Company> {
    // แปลงข้อมูลจาก frontend format เป็น backend format
    // สำหรับการ update ไม่จำเป็นต้องส่ง code เพราะเราไม่สามารถเปลี่ยนได้
    const companyData: any = {
      name: data.name,
      tax_id: data.taxId,  // เปลี่ยนจาก taxId เป็น tax_id
      address: data.address
    };

    // ฟิลด์ optional 3 ตัวนี้ "มีช่องกรอกจริง" บนฟอร์มบริษัท จึงต้องล้างค่าได้ด้วย
    // guard แบบ truthiness เดิม (`if (data.phone && data.phone.trim())`) ทำให้ผู้ใช้ลบข้อความ
    // ในช่องแล้วกดบันทึก กลายเป็น no-op เงียบ ๆ — ค่าเก่าเด้งกลับมาเหมือนไม่ได้กดบันทึก
    // (เช่น ผู้ติดต่อลาออก อยากล้างชื่อ/เบอร์เก่าทิ้ง ทำไม่ได้เลย)
    // ส่งเมื่อ field ถูกระบุมา — สตริงว่าง = ตั้งใจล้าง (คนละเคสกับ C8 ที่ฟอร์ม "ไม่มีช่อง")
    if (data.contactPerson !== undefined && data.contactPerson !== null) {
      companyData.contact_person = data.contactPerson.trim();
    }
    if (data.phone !== undefined && data.phone !== null) {
      companyData.phone = data.phone.trim();
    }
    if (data.email !== undefined && data.email !== null) {
      companyData.email = data.email.trim();
    }
    // เพิ่มการส่ง deposit amount (รองรับค่า 0)
    if (data.depositSettings && typeof data.depositSettings.deposit_amount === 'number') {
      companyData.deposit_amount = data.depositSettings.deposit_amount;
    }
    // เพิ่ม field ใหม่
    if (data.sales_person_id !== undefined) {
      companyData.sales_person_id = data.sales_person_id;
    }
    if (data.is_active !== undefined) {
      companyData.is_active = data.is_active;
    }
    // เพิ่ม default WHT rate
    if (data.default_wht_rate !== undefined) {
      companyData.default_wht_rate = data.default_wht_rate;
    }
    // เพิ่ม billing fields
    if (data.billing_day !== undefined) {
      companyData.billing_day = data.billing_day;
    }
    if (data.payment_terms !== undefined) {
      companyData.payment_terms = data.payment_terms;
    }
    // KD3 S2496 (FLOW-MD-014): ฟอร์มมีช่อง "วันครบกำหนดชำระ (ของเดือนถัดไป)" และ "ประเภทลูกค้า" แต่ payload ไม่เคยส่ง
    // → กดบันทึกขึ้น "สำเร็จ" แต่ค่าที่เลือกหายเงียบ (DB คงค่าเดิม/NULL) · ส่งเมื่อ key มีมา (null/'' = ตั้งใจล้าง)
    if (data.due_day_of_next_month !== undefined) {
      companyData.due_day_of_next_month = data.due_day_of_next_month ?? null;
    }
    if (data.customer_type !== undefined) {
      companyData.customer_type = data.customer_type || null;
    }

    console.log('UPDATE - depositSettings:', data.depositSettings);
    console.log('UPDATE - Final companyData:', companyData);
    
    const response = await ApiWithLogging.put<Company>(`/companies/${id}`, companyData, ifMatch ? { headers: { 'If-Match': ifMatch } } : {},
      {
        service: 'companyService',
        action: 'update_company',
        entityType: 'company',
        entityId: id,
        details: {
          name: data.name,
          taxId: data.taxId
        }
      }
    );
    
    return response;
  },

  // ลบบริษัท
  async deleteCompany(id: number): Promise<any> {
    try {
      // ดำเนินการลบบริษัท โดยตอนนี้ API จะตรวจสอบความเชื่อมโยงเอง
      // และตอบกลับด้วย success: false ถ้าไม่สามารถลบได้
      const response = await ApiWithLogging.delete(`/companies/${id}`, {},
        {
          service: 'companyService',
          action: 'delete_company',
          entityType: 'company',
          entityId: id
        }
      );
      
      // ตรวจสอบผลการลบจาก response
      if (response.data && response.data.success === false) {
        // API ส่งสถานะสำเร็จแต่ไม่สามารถลบบริษัทได้
        const error = new Error(response.data.message || 'ไม่สามารถลบบริษัทได้');
        // เพิ่มข้อมูลเพิ่มเติมเกี่ยวกับบริษัทและความสัมพันธ์
        (error as any).companyData = response.data.company;
        (error as any).relatedInfo = response.data.related_info;
        throw error;
      }
      
      // ถ้าลบสำเร็จ
      return response;
    } catch (error: any) {
      // กรณีที่ API ตอบกลับด้วย Error เช่น 404 หรือ 500
      if (error.response) {
        // ถ้า API ส่งข้อมูลกลับมาแม้จะมี error
        if (error.response.data) {
          // ถ้ามีข้อความแจ้งเตือนจาก API ให้ใช้ข้อความนั้น
          if (error.response.data.message) {
            error.message = error.response.data.message;
            
            // เก็บข้อมูลเพิ่มเติมเกี่ยวกับบริษัทและความสัมพันธ์ถ้ามี
            if (error.response.data.company) {
              (error as any).companyData = error.response.data.company;
            }
            if (error.response.data.related_info) {
              (error as any).relatedInfo = error.response.data.related_info;
            }
          }
          // ถ้าไม่มี message แต่มี detail
          else if (error.response.data.detail) {
            if (typeof error.response.data.detail === 'string') {
              error.message = error.response.data.detail;
            }
          }
        }
      }
      
      // ตรวจสอบข้อความ error ทั้งหมด
      if (error.message && typeof error.message === 'string') {
        if (error.message.includes('Request failed with status code')) {
          // เปลี่ยน error message ที่เป็น default ของ axios ให้มีความหมายมากขึ้น
          error.message = 'ไม่สามารถลบบริษัทได้เนื่องจากเกิดข้อผิดพลาดในการเชื่อมต่อกับเซิร์ฟเวอร์';
        } else if (error.message.includes('Unknown column') && error.message.includes('flowaccount_id')) {
          // จัดการกับข้อผิดพลาดเฉพาะของ SQL เกี่ยวกับ flowaccount_id
          error.message = 'ไม่สามารถลบบริษัทได้เนื่องจากเกิดข้อผิดพลาดในฐานข้อมูล (โครงสร้างตารางไม่ตรงกัน)';
          error.isSqlSchemaError = true;
        } else if (error.message.includes('pymysql.err.OperationalError')) {
          // จัดการข้อผิดพลาดจาก SQL ทั่วไป
          error.message = 'ไม่สามารถลบบริษัทได้เนื่องจากเกิดข้อผิดพลาดในฐานข้อมูล';
          error.isSqlError = true;
        }
      }
      
      throw error;
    }
  },

  // ซิงค์ข้อมูลกับ Flow Account
  async syncWithFlowAccount(): Promise<{ message: string; count: number }> {
    const response = await ApiWithLogging.post<{ message: string; count: number }>('/sync/companies', {}, 
      { timeout: 120000 }, // 2 minutes timeout สำหรับ sync operation
      {
        service: 'companyService',
        action: 'sync_with_flow_account',
        entityType: 'company'
      }
    );
    return response;
  },

  // ดึงข้อมูลค่ามัดจำรวมของบริษัท (เพดาน / ใช้ไป / เหลือ) — ใช้แสดง panel วงเงินในฟอร์มเช่า
  // หมายเหตุ: getDepositSettings/updateDepositSettings ถูกลบทิ้งแล้ว เพราะ backend ไม่มี route ดังกล่าว
  // (เคย fail เงียบ) — เพดานวงเงินตั้งผ่าน field deposit_amount ของ company create/update ตามปกติ
  async getTotalDeposit(companyId: number): Promise<CompanyTotalDepositInfo> {
    const response = await ApiWithLogging.get<CompanyTotalDepositInfo>(`/companies/${companyId}/total-deposit`, {},
      {
        service: 'companyService',
        action: 'get_total_deposit',
        entityType: 'company',
        entityId: companyId
      }
    );
    return response;
  },

  // ตรวจสอบว่ารหัสบริษัทซ้ำหรือไม่
  async checkCodeExists(code: string): Promise<boolean> {
    try {
      const response = await ApiWithLogging.get<GetCompaniesResponse>('/companies/', 
        { 
          params: {
            code: code,
            limit: 1000
          }
        },
        {
          service: 'companyService',
          action: 'check_code_exists',
          entityType: 'company'
        }
      );
      
      // KD3 S2126 (BUG-S2126-001): ตัวกรอง backend เป็น substring (ilike %x%) → เทียบรหัสตรงตัวเอง
      // (ไม่สนตัวพิมพ์ตาม collation ของ DB) · ดึงหลายแถวไม่งั้นแถวที่ตรงจริงอาจไม่อยู่หน้าแรก
      return isExactCodeMatch(response.items.map(c => c.code), code);
    } catch (error) {
      console.error('Error checking company code:', error);
      return false;
    }
  },
  // ส่งออกข้อมูลบริษัทเป็น Excel
  // ส่งออก Excel — ต้องส่งตัวกรองที่หน้าจอใช้อยู่ไปด้วย ไม่งั้นผู้ใช้ค้นหา/กรองแล้วกดส่งออก
  // จะได้ไฟล์ "ทั้งระบบ" โดยไม่มีอะไรเตือน (GAP-EXPORT-FILTER)
  // รับชุดเดียวกับ getCompanies (รวม Advanced Search 10 ช่อง) เพราะ backend รับครบแล้ว
  // — ถ้าส่งแค่ search + is_active ผู้ใช้ที่กรองด้วยช่อง advanced จะยังได้ไฟล์ปนเหมือนเดิม
  async exportToExcel(params: GetCompaniesParams = {}): Promise<Blob> {
    const query: any = {};
    // ไล่ทีละคีย์ให้ตรงกับ signature ของ endpoint (ไม่ spread ทั้งก้อน เพราะ params ของ
    // หน้ารายการมี page/limit/_t ที่ export ไม่ต้องใช้ และจะกลายเป็น query ขยะ)
    for (const key of [
      'search', 'name', 'code', 'tax_id', 'address', 'contact_person', 'phone',
      'email', 'is_active', 'sales_person_id', 'synced_from_flow',
      'created_from', 'created_to',
    ] as const) {
      const value = (params as any)[key];
      if (value !== undefined && value !== null && value !== '') query[key] = value;
    }

    const response = await ApiWithLogging.get('/companies/export/excel',
      {
        responseType: 'blob',
        params: query
      },
      {
        service: 'companyService',
        action: 'export_companies_excel',
        entityType: 'company'
      }
    );
    return response;
  }
};

// Named exports สำหรับ React Query hooks
export const getCompanies = companyService.getCompanies;
export const getCompany = companyService.getCompany;
export const createCompany = companyService.createCompany;
export const updateCompany = companyService.updateCompany;
export const deleteCompany = companyService.deleteCompany;
export const syncFlowAccountCompanies = companyService.syncWithFlowAccount;

export default companyService;